import React, { useMemo, useState } from 'react';
import FilterBar from './FilterBar';
import {
  formatCurrency,
  formatDate,
  matchesDateRange,
  normalizeSearch,
  triggerCsvDownload,
  triggerExcelLikeDownload,
} from './utils';
import TablePagination, { usePagination } from '../TablePagination';

export default function CollectionsTab({
  collections,
  sales,
  selectedBranch,
  isLoading,
  onViewSale,
}) {
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const filteredRows = useMemo(() => {
    const searchKey = normalizeSearch(search);

    return collections
      .filter((row) => String(row.status || '') !== 'Cancelled')
      .filter((row) => selectedBranch === 'all' || String(row.branch || '') === selectedBranch)
      .filter((row) => {
        if (!searchKey) {
          return true;
        }

        const text = [
          row.client_name,
          row.product,
          row.agent_name,
          row.ar_si_no,
          row.transaction_no,
        ]
          .map((value) => normalizeSearch(value))
          .join(' ');

        return text.includes(searchKey);
      })
      .filter((row) => matchesDateRange(row.payment_date || row.date, dateFrom, dateTo));
  }, [collections, search, selectedBranch, dateFrom, dateTo]);

  const pagination = usePagination(filteredRows);

  function exportCsv() {
    triggerCsvDownload(
      `collections-${new Date().toISOString().slice(0, 10)}.csv`,
      ['Date', 'Transaction No.', 'AR/SI No.', 'Client', 'Product', 'Agent', 'Branch', 'Mode', 'Paid Premium'],
      filteredRows.map((row) => [
        formatDate(row.payment_date || row.date),
        row.transaction_no || '',
        row.ar_si_no || '',
        row.client_name || '',
        row.product || '',
        row.agent_name || '',
        row.branch || '',
        row.mode_of_payment || '',
        row.premium_amount ?? 0,
      ])
    );
  }

  function exportExcel() {
    triggerExcelLikeDownload(
      `collections-${new Date().toISOString().slice(0, 10)}.xls`,
      ['Date', 'Transaction No.', 'AR/SI No.', 'Client', 'Product', 'Agent', 'Branch', 'Mode', 'Paid Premium'],
      filteredRows.map((row) => [
        formatDate(row.payment_date || row.date),
        row.transaction_no || '',
        row.ar_si_no || '',
        row.client_name || '',
        row.product || '',
        row.agent_name || '',
        row.branch || '',
        row.mode_of_payment || '',
        row.premium_amount ?? 0,
      ])
    );
  }

  return (
    <div className="collections-tab-content">
      <FilterBar
        search={search}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onSearchChange={setSearch}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        onExportCsv={exportCsv}
        onExportExcel={exportExcel}
      />

      {isLoading ? (
        <div className="dashboard-loading">Loading collections...</div>
      ) : filteredRows.length === 0 ? (
        <div className="dashboard-empty">No collections found.</div>
      ) : (
        <div className="dashboard-table-wrap">
          <table className="dashboard-table collections-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Transaction No.</th>
                <th>AR / SI No.</th>
                <th>Client</th>
                <th>Product</th>
                <th>Agent</th>
                <th>Branch</th>
                <th>Mode</th>
                <th>Paid Premium</th>
                <th>Proof</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {pagination.pagedItems.map((row) => {
                const sale = sales.find((item) => Number(item.id) === Number(row.sale_id)) || null;

                return (
                  <tr key={row.id}>
                    <td>{formatDate(row.payment_date || row.date)}</td>
                    <td className="collections-mono-cell">{row.transaction_no || '-'}</td>
                    <td className="collections-mono-cell">{row.ar_si_no || '-'}</td>
                    <td>{row.client_name || '-'}</td>
                    <td>{row.product || '-'}</td>
                    <td>
                      <div>{row.agent_name || '-'}</div>
                      {row.agent_position ? <div className="collections-subline">{row.agent_position}</div> : null}
                    </td>
                    <td>{row.branch || '-'}</td>
                    <td>{row.mode_of_payment || '-'}</td>
                    <td className="collections-number">{formatCurrency(row.premium_amount)}</td>
                    <td>
                      {row.proof_of_payment_url ? (
                        <a href={row.proof_of_payment_url} target="_blank" rel="noreferrer">
                          {row.proof_of_payment_name || 'View'}
                        </a>
                      ) : '-'}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="profile-secondary-btn profile-secondary-btn-xs"
                        onClick={() => onViewSale(sale)}
                        disabled={!sale}
                      >
                        View Sale
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!isLoading ? <TablePagination {...pagination} /> : null}
    </div>
  );
}
